import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * ShiftRequestsInbox — covers swap / cover / drop requests.
 *
 * Props:
 *  - mode: 'staff' | 'admin'
 *      'staff': sees their own requests + open cover requests they can claim
 *               or named-swap requests they need to respond to.
 *      'admin': sees everything in the org with approve/reject (mutates roster).
 */

const TYPE_TONES = {
  swap: { label: '🔄 Swap', tone: 'hsl(220 70% 55%)' },
  cover: { label: '🆘 Cover', tone: 'hsl(35 95% 50%)' },
  drop: { label: '🚪 Drop', tone: 'hsl(0 70% 55%)' },
};

const STATUS_TONES = {
  open: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)', label: '🟡 Open' },
  accepted: { bg: 'hsl(190 65% 92%)', fg: 'hsl(190 65% 25%)', label: '👍 Accepted' },
  approved: { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: '✅ Approved' },
  rejected: { bg: 'hsl(0 70% 94%)', fg: 'hsl(0 60% 35%)', label: '❌ Rejected' },
  cancelled: { bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 30%)', label: '✕ Cancelled' },
};

const fmtDate = (s) => {
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return s; }
};

export function ShiftRequestsInbox({ mode = 'staff' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [myShifts, setMyShifts] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [form, setForm] = useState({
    shift_id: '', type: 'cover', target_staff_id: '', message: '',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getShiftRequests();
      setRows(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFormSources = async () => {
    // Staff: load their own upcoming published shifts to populate the picker.
    try {
      const today = new Date();
      const future = new Date(); future.setDate(future.getDate() + 60);
      const iso = (d) => d.toISOString();
      const shifts = await api.getShifts(iso(today), iso(future));
      // Backend returns staff_name; we trust the GET filter is tenant-scoped.
      // For staff mode, the list already includes only their org's shifts;
      // we filter to those assigned to them by matching the staff_name field
      // we render below — but to be safe we pass them all and let them pick
      // (server-side validation will reject if not their shift).
      setMyShifts(shifts || []);
    } catch (err) {
      console.error('Failed to load shifts', err);
    }
    try {
      // For swap target picker we need other staff in the org. Reuse the
      // staff_profiles endpoint which is already tenant-scoped.
      const profiles = await api.getStaffProfiles();
      setStaffOptions((profiles || []).map((p) => ({
        id: p._id,
        name: p.user_name || p.user_email || p._id,
      })));
    } catch {
      // Staff endpoint may forbid — that's fine, swap just won't pre-populate.
    }
  };

  useEffect(() => {
    load();
    if (mode === 'staff') loadFormSources();
  }, [mode]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.shift_id) {
      setError('Pick a shift');
      return;
    }
    if (form.type === 'swap' && !form.target_staff_id) {
      setError('Swap requests need a target colleague');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.createShiftRequest({
        shift_id: form.shift_id,
        type: form.type,
        target_staff_id: form.target_staff_id || null,
        message: form.message || null,
      });
      setForm({ shift_id: '', type: 'cover', target_staff_id: '', message: '' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const adminApprove = async (r) => {
    const note = window.prompt('Optional note for the audit log:', '');
    if (note === null) return;
    try { await api.approveShiftRequest(r._id, note); load(); }
    catch (err) { alert(err.message); }
  };
  const adminReject = async (r) => {
    const note = window.prompt('Reason for rejection:', '');
    if (note === null) return;
    try { await api.rejectShiftRequest(r._id, note); load(); }
    catch (err) { alert(err.message); }
  };
  const counterAccept = async (r) => {
    if (!window.confirm(`Accept this ${r.type} request?`)) return;
    try { await api.respondShiftRequest(r._id, true); load(); }
    catch (err) { alert(err.message); }
  };
  const counterDecline = async (r) => {
    try { await api.respondShiftRequest(r._id, false); load(); }
    catch (err) { alert(err.message); }
  };
  const cancel = async (r) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await api.cancelShiftRequest(r._id); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div data-testid={`shift-requests-${mode}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ marginTop: 0 }}>{mode === 'admin' ? '🔁 Shift Change Requests' : '🔁 Shift Requests'}</h2>
        {mode === 'staff' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { setShowForm((v) => !v); setError(null); }}
            data-testid="shift-req-toggle-form"
          >
            {showForm ? 'Close' : '+ New request'}
          </button>
        )}
      </div>
      {error && <div className="error-message" data-testid="shift-req-error">{error}</div>}

      {mode === 'staff' && showForm && (
        <form
          onSubmit={handleCreate}
          className="card"
          style={{ padding: '1rem', borderStyle: 'dashed', marginBottom: '1rem' }}
          data-testid="shift-req-form"
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Shift</label>
              <select
                required
                value={form.shift_id}
                onChange={(e) => setForm({ ...form, shift_id: e.target.value })}
                data-testid="shift-req-shift"
              >
                <option value="">— Select your shift —</option>
                {myShifts.map((s) => {
                  const d = new Date(s.date);
                  return (
                    <option key={s._id} value={s._id}>
                      {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} {s.start_time}–{s.end_time} {s.room_name ? `· ${s.room_name}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="shift-req-type"
              >
                <option value="cover">🆘 Cover (anyone can pick this up)</option>
                <option value="swap">🔄 Swap (with a named colleague)</option>
                <option value="drop">🚪 Drop (no replacement needed — admin will reroute)</option>
              </select>
            </div>
            {form.type === 'swap' && (
              <div className="form-group" style={{ margin: 0 }}>
                <label>Swap with</label>
                <select
                  required
                  value={form.target_staff_id}
                  onChange={(e) => setForm({ ...form, target_staff_id: e.target.value })}
                  data-testid="shift-req-target"
                >
                  <option value="">— Pick a colleague —</option>
                  {staffOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <label>Message (optional)</label>
              <input
                type="text"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="e.g. Hospital appointment, can swap to Wednesday"
                data-testid="shift-req-message"
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="shift-req-save">
              {saving ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="shift-req-empty">No shift requests in this view.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {rows.map((r) => {
            const tt = TYPE_TONES[r.type] || TYPE_TONES.cover;
            const st = STATUS_TONES[r.status] || STATUS_TONES.open;
            return (
              <div
                key={r._id}
                className="card"
                data-testid={`shift-req-row-${r._id}`}
                style={{ padding: '0.85rem 1rem' }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span
                        className="badge"
                        style={{
                          background: `${tt.tone}15`, color: tt.tone, fontWeight: 600,
                          padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem',
                        }}
                      >
                        {tt.label}
                      </span>
                      <span
                        className="badge"
                        style={{
                          background: st.bg, color: st.fg, fontWeight: 600,
                          padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem',
                        }}
                        data-testid={`shift-req-status-${r._id}`}
                      >
                        {st.label}
                      </span>
                    </div>
                    <div style={{ marginTop: '0.4rem' }}>
                      <strong>{r.requester_name}</strong> on{' '}
                      {r.shift ? (
                        <>
                          <strong>{fmtDate(r.shift.date)}</strong> ({r.shift.start_time}–{r.shift.end_time})
                        </>
                      ) : 'a shift'}
                      {r.target_name && (
                        <>
                          {' '}→ <strong>{r.target_name}</strong>
                        </>
                      )}
                    </div>
                    {r.message && (
                      <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                        “{r.message}”
                      </div>
                    )}
                    {r.review_note && (
                      <div style={{ fontSize: '0.8rem', marginTop: '0.2rem', color: 'hsl(var(--muted-foreground))' }}>
                        Admin note: {r.review_note}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {mode === 'admin' && (r.status === 'open' || r.status === 'accepted') && (
                      <>
                        <button className="btn btn-primary" onClick={() => adminApprove(r)} data-testid={`shift-req-approve-${r._id}`}>
                          ✅ Approve & apply
                        </button>
                        <button className="btn btn-secondary" onClick={() => adminReject(r)} data-testid={`shift-req-reject-${r._id}`}>
                          ❌ Reject
                        </button>
                      </>
                    )}
                    {mode === 'staff' && r.status === 'open' && r.type !== 'drop' && (
                      <>
                        <button className="btn btn-primary" onClick={() => counterAccept(r)} data-testid={`shift-req-accept-${r._id}`}>
                          {r.type === 'swap' ? 'Accept swap' : 'I can cover'}
                        </button>
                        <button className="btn btn-ghost" onClick={() => counterDecline(r)} data-testid={`shift-req-decline-${r._id}`}>
                          Decline
                        </button>
                      </>
                    )}
                    {mode === 'staff' && r.status === 'open' && (
                      <button className="btn btn-ghost" onClick={() => cancel(r)} data-testid={`shift-req-cancel-${r._id}`}>
                        Cancel my request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ShiftRequestsInbox;
